/* eslint-disable */
import type { RequestContext } from "../RequestContextProto.pb";
import type { ResponseContext } from "../ResponseContextProto.pb";
import type { CashCustomerProfile } from "../api/cash_customer_profile.pb";
import type { CashtagStatus } from "../api/cashtag_status.pb";

/** The request to get the public cashtag profile information. */
export interface GetCashtagProfileRequest {
  /** The unprefixed cashtag (no leading currency symbol) */
  cashtag?: string;
}

export interface GetCashtagProfileResponse {
  status?: GetCashtagProfileResponseStatus;
  /** The public profile, if it exists and is enabled. */
  profile?: CashCustomerProfile;
}

export enum GetCashtagProfileResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  NOT_PUBLIC = "NOT_PUBLIC",
}

/** The request to get the status and availability of a cashtag. */
export interface GetCashtagStatusRequest {
  cashtag_candidate?: string;
  cashtag_restriction?: GetCashtagStatusRequestCashtagRestriction;
}

export enum GetCashtagStatusRequestCashtagRestriction {
  DEFAULT = "DEFAULT",
  /** NO_CASHTAG_REUSE - Returns unavailable if the cashtag matches the active cashtag. */
  NO_CASHTAG_REUSE = "NO_CASHTAG_REUSE",
}

export interface GetCashtagStatusResponse {
  status?: GetCashtagStatusResponseStatus;
  /** The same value as passed in the request. */
  cashtag_candidate?: string;
  /** The status of the requested cashtag. */
  cashtag_status?: CashtagStatus;
  /** The text to display for the cashtag_url. e.g. "cash.me/$noble" */
  cashtag_url_display_text?: string;
  /** The failure_message will be populated when the cashtag_status is not AVAILABLE. */
  failure_message?: string;
  /**
   * When non-null, the app should present a dialog with the confirm_message before allowing
   * the user to select this cashtag.
   * e.g. "Are you sure you want to change your Cashtag? This is the last time you can do this."
   */
  confirm_message?: string;
}

export enum GetCashtagStatusResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
}

/** The request to set the cashtag for a customer. */
export interface SetCashtagRequest {
  request_context?: RequestContext;
  cashtag?: string;
}

export interface SetCashtagResponse {
  response_context?: ResponseContext;
  status?: SetCashtagResponseStatus;
  /** The normalized URL e.g. "https://cash.me/$noble" */
  cashtag_url?: string;
  /** The text to display for the cashtag_url. e.g. "cash.me/$noble" */
  cashtag_url_display_text?: string;
  /**
   * If the response status is CASHTAG_UNAVAILABLE, this field will be populated with the status
   * of the cashtag.
   */
  cashtag_status?: CashtagStatus;
  failure_reason?: SetCashtagResponseFailureReason;
}

export enum SetCashtagResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CASHTAG_STATUS_FAILURE - The cashtag is invalid or no longer available. Check the cashtag status for details. The
   * failure message contains a message to display to the customer.
   */
  CASHTAG_STATUS_FAILURE = "CASHTAG_STATUS_FAILURE",
  /**
   * FAILURE - The cashtag was available but the request failed for another reason. The failure message
   * contains a message to display to the customer. The failure can be due to the customer not
   * having a debit card, or they may have exceeded a limit on the number of cashtags that can be
   * linked to their customer or debit card.
   */
  FAILURE = "FAILURE",
}

/**
 * The failure reason is to elaborate on the FAILURE status. This allows backwards compatibility but also allows us
 * to differentiate between the different types of failures to make sure we are presenting the correct button text
 * and/or support article to the customer.
 */
export enum SetCashtagResponseFailureReason {
  TOO_MANY_CASHTAGS = "TOO_MANY_CASHTAGS",
  ACCOUNT_DENYLISTED = "ACCOUNT_DENYLISTED",
}

/** The request to get a suggested cashtag for a customer. */
export interface GetCashtagSuggestionRequest {
}

export interface GetCashtagSuggestionResponse {
  status?: GetCashtagSuggestionResponseStatus;
  /** A suggested cashtag. This can be null if there is no good suggestion. */
  cashtag_suggestion?: string;
}

export enum GetCashtagSuggestionResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to enable or disable the public profile page for the customer's cashtag. */
export interface SetCashtagUrlEnabledRequest {
  enabled?: boolean;
  request_context?: RequestContext;
}

export interface SetCashtagUrlEnabledResponse {
  status?: SetCashtagUrlEnabledResponseStatus;
  response_context?: ResponseContext;
}

export enum SetCashtagUrlEnabledResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request to set a customer's cashtag for Treehouse flows. */
export interface ExecuteSetCashtagRequest {
  /** customer's cashtag. */
  cashtag?: string;
}

/** Response to a request to set a customer's cashtag for Treehouse flows. */
export interface ExecuteSetCashtagResponse {
  /** Returns whether a customer's cashtag has been successfully set. */
  status?: ExecuteSetCashtagResponseStatus;
  /** Returns the failure message - equivalent of dialog_message in Plasma flows. */
  failure_message?: string;
}

export enum ExecuteSetCashtagResponseStatus {
  ERROR = "ERROR",
  SUCCESS = "SUCCESS",
}
